import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr } from "../lib/api";
import { Fire, CheckCircle, Sparkle, PaperPlaneTilt, ArrowsClockwise, X, Warning, CaretDown, CaretRight, Eye, CursorClick, WarningCircle } from "@phosphor-icons/react";

const EVENT_META = {
  sent: [PaperPlaneTilt, "text-zinc-400", "Sent"],
  open: [Eye, "text-sky-600", "Opened"],
  click: [CursorClick, "text-orange-600", "Clicked"],
  followup: [PaperPlaneTilt, "text-emerald-600", "Follow-up email"],
  bounce: [WarningCircle, "text-red-500", "Bounced"],
  unsubscribe: [WarningCircle, "text-amber-600", "Unsubscribed"],
};

const TimelineRow = ({ ev }) => {
  const [Icon, color, label] = EVENT_META[ev.type] || [PaperPlaneTilt, "text-zinc-400", ev.type];
  return (
    <li className="flex items-start gap-2 py-1">
      <Icon size={13} className={`${color} mt-0.5 shrink-0`} />
      <div className="min-w-0 flex-1 text-[11px] text-zinc-600">
        <span className="font-medium text-zinc-800">{label}</span>
        {ev.campaign_name ? <span className="text-zinc-500"> · {ev.campaign_name}</span> : null}
        {ev.type === "click" && ev.url ? <span className="block truncate text-zinc-400">{(ev.url || "").split("?utm_")[0]}</span> : null}
        {ev.type === "followup" && ev.subject ? <span className="block truncate text-zinc-400">"{ev.subject}"</span> : null}
      </div>
      <span className="shrink-0 text-[10px] text-zinc-400">{new Date(ev.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
    </li>
  );
};
import { toast } from "sonner";
import { Modal, ModalFooter } from "../pages/Contacts";

const timeAgo = (iso) => {
  if (!iso) return "—";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

const FollowUpDialog = ({ lead, onClose, onSent }) => {
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [li, setLi] = useState(null);
  const [adopted, setAdopted] = useState(false);

  const gen = useCallback(async (instr) => {
    setLoading(true);
    try {
      const d = await api.hotLeadDraft(lead.id, instr || undefined);
      setDraft({ subject: d.subject, body: d.body });
      setLi(d.linkedin_check || null);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => { gen(); }, [gen]);

  const send = async () => {
    if (!draft.subject.trim() || !draft.body.trim()) { toast.error("Subject and body are required"); return; }
    setSending(true);
    try {
      const res = await api.hotLeadSend(lead.id, draft);
      toast.success(res.channel === "m365"
        ? `Sent from your Medicys mailbox to ${lead.contact.email} (Exclaimer signature applied, copy in Sent Items)`
        : res.simulated ? "Follow-up simulated — no Resend key in this environment" : `Follow-up sent to ${lead.contact.email}`);
      onSent();
    } catch (e) {
      toast.error(fmtErr(e));
      setSending(false);
    }
  };

  const adopt = async () => {
    try {
      const patch = {};
      if (li.mismatch?.title) patch.job_title = li.title;
      if (li.mismatch?.company) patch.company = li.company;
      await api.updateContact(lead.contact.id, patch);
      setAdopted(true);
      toast.success("Contact updated with the LinkedIn role");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const name = `${lead.contact.first_name || ""} ${lead.contact.last_name || ""}`.trim() || lead.contact.email;
  return (
    <Modal wide title={`Follow up with ${name}`} subtitle={`${lead.contact.email} · clicked ${lead.clicks}× in "${lead.campaign_name}" · AI drafted this, edit freely before sending`} onClose={onClose}>
      {loading ? (
        <div className="py-10 text-center text-sm text-zinc-500" data-testid="followup-loading">
          <Sparkle size={20} className="mx-auto mb-2 text-orange-500 animate-pulse" />
          Writing a personal follow-up based on what they clicked…
        </div>
      ) : (
        <div className="space-y-3">
          {li?.matched && li.mismatch && !adopted && (
            <div data-testid="followup-linkedin-alert" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
              <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <span className="font-semibold">LinkedIn differs from the MediHub record.</span>{" "}
                LinkedIn shows <b>{[li.title, li.company].filter(Boolean).join(" at ")}</b>; MediHub has <b>{[lead.contact.job_title, lead.contact.company].filter(Boolean).join(" at ") || "no role recorded"}</b>. The draft is written for the LinkedIn version.
              </div>
              <button onClick={adopt} data-testid="followup-adopt-linkedin"
                className="shrink-0 text-xs font-medium px-2 py-1 rounded-md border border-amber-400 bg-white text-amber-800 hover:bg-amber-100">
                Update MediHub record
              </button>
            </div>
          )}
          {li?.matched && li.mismatch && adopted && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="followup-linkedin-adopted">
              <CheckCircle size={12} weight="fill" /> MediHub record updated to match LinkedIn
            </div>
          )}
          {li?.matched && !li.mismatch && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="followup-linkedin-ok">
              <CheckCircle size={12} weight="fill" /> LinkedIn profile checked{li.cached ? " (cached)" : ""}: matches the MediHub record
            </div>
          )}
          <div>
            <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Subject</span>
            <input data-testid="followup-subject" value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              className="h-9 w-full rounded-md border border-zinc-200 px-3 text-sm" />
          </div>
          <div>
            <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Message (plain personal email — no logo or marketing footer)</span>
            <textarea data-testid="followup-body" rows={10} value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm leading-relaxed" />
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="followup-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)}
              placeholder="Optional: tell the AI what to change (e.g. mention our GLP-1 report, shorter)"
              className="h-9 flex-1 rounded-md border border-zinc-200 px-3 text-xs" />
            <button onClick={() => gen(instructions)} disabled={loading}
              data-testid="followup-regenerate"
              className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-2 rounded-md border border-zinc-200 text-zinc-700 hover:bg-zinc-50">
              <ArrowsClockwise size={13} /> Regenerate
            </button>
          </div>
        </div>
      )}
      <ModalFooter onClose={onClose}>
        <button onClick={send} disabled={loading || sending}
          data-testid="followup-send"
          className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
          <PaperPlaneTilt size={14} /> {sending ? "Sending…" : "Send follow-up"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export const HotLeadsPanel = () => {
  const [tab, setTab] = useState("new");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [followUp, setFollowUp] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [timelines, setTimelines] = useState({});

  const toggleExpand = (h) => {
    const next = expanded === h.id ? null : h.id;
    setExpanded(next);
    if (next && !timelines[h.contact_id]) {
      api.contactTimeline(h.contact_id).then(d => setTimelines(t => ({ ...t, [h.contact_id]: d.items || [] }))).catch(() => {});
    }
  };

  const load = useCallback((status) => {
    api.hotLeads(status).then(setData).catch(() => {});
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const markContacted = async (id) => {
    setBusy(id);
    try {
      await api.hotLeadContacted(id);
      toast.success("Marked as contacted");
      load(tab);
    } catch {
      toast.error("Could not update hot lead");
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (id) => {
    setBusy(id);
    try {
      await api.hotLeadDismiss(id);
      toast.success("Discarded. They won't reappear for this campaign");
      load(tab);
    } catch {
      toast.error("Could not discard hot lead");
    } finally {
      setBusy(null);
    }
  };

  if (!data) return null;
  const items = data.items || [];
  const newCount = data.counts?.new ?? 0;

  return (
    <div className="rounded-lg border border-orange-200 bg-gradient-to-br from-orange-50/70 to-white p-5" data-testid="hot-leads-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-md bg-orange-100 flex items-center justify-center shrink-0">
            <Fire size={15} weight="fill" className="text-orange-600" />
          </span>
          <div>
            <h3 className="font-heading text-base font-medium text-zinc-950">Hot leads — follow up today</h3>
            <p className="text-xs text-zinc-500">Anyone who clicked 2+ times in a single campaign (scanner clicks excluded).</p>
          </div>
        </div>
        <div className="inline-flex rounded-md border border-orange-200 bg-white p-0.5">
          <button onClick={() => setTab("new")} data-testid="hot-leads-tab-new"
            className={`px-2.5 py-1 text-xs font-medium rounded ${tab === "new" ? "bg-orange-600 text-white" : "text-zinc-600 hover:bg-orange-50"}`}>
            To follow up{newCount > 0 ? ` (${newCount})` : ""}
          </button>
          <button onClick={() => setTab("contacted")} data-testid="hot-leads-tab-contacted"
            className={`px-2.5 py-1 text-xs font-medium rounded ${tab === "contacted" ? "bg-orange-600 text-white" : "text-zinc-600 hover:bg-orange-50"}`}>
            Contacted{data.counts?.contacted ? ` (${data.counts.contacted})` : ""}
          </button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4 text-center" data-testid="hot-leads-empty">
          {tab === "new" ? "No hot leads waiting — new ones appear here the moment someone clicks twice." : "No leads marked contacted yet."}
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-orange-100">
          {items.map(h => (
            <li key={h.id} className="py-2.5" data-testid={`hot-lead-row-${h.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => toggleExpand(h)} data-testid={`hot-lead-expand-${h.id}`}
                title="Show interaction timeline"
                className="shrink-0 text-zinc-400 hover:text-zinc-700 p-0.5 rounded">
                {expanded === h.id ? <CaretDown size={13} /> : <CaretRight size={13} />}
              </button>
              <Fire size={14} weight="fill" className="text-orange-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <Link to={`/contacts/${h.contact.id}`} className="text-sm font-medium text-zinc-900 hover:underline">
                  {`${h.contact.first_name || ""} ${h.contact.last_name || ""}`.trim() || h.contact.email}
                </Link>
                <span className="ml-2 text-xs text-zinc-500">{[h.contact.job_title, h.contact.company].filter(Boolean).join(" · ")}</span>
                {h.contact.status !== "active" && (
                  <span data-testid={`hot-lead-status-${h.id}`} className="ml-2 text-[10px] text-amber-700 border border-amber-200 bg-amber-50 rounded px-1 py-px align-middle">{h.contact.status.replace("_", " ")}</span>
                )}
                <div className="text-[11px] text-zinc-500 truncate">
                  <span className="text-orange-700 font-semibold">{h.clicks}× clicks</span>
                  {" in "}
                  <Link to={`/campaigns/${h.campaign_id}`} className="hover:underline text-zinc-600">{h.campaign_name}</Link>
                  {" · last click "}{timeAgo(h.last_click_at)}
                  {h.status === "contacted" && h.contacted_at ? ` · contacted ${timeAgo(h.contacted_at)}${h.contacted_by ? ` by ${h.contacted_by}` : ""}${h.followed_up ? " · follow-up emailed" : ""}` : ""}
                </div>
              </div>
              {h.status === "new" ? (
                <div className="shrink-0 flex items-center gap-1.5">
                  {h.contact.status === "active" && (
                    <button onClick={() => setFollowUp(h)}
                      data-testid={`hot-lead-followup-${h.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700">
                      <Sparkle size={13} weight="fill" /> AI follow-up
                    </button>
                  )}
                  <button onClick={() => markContacted(h.id)} disabled={busy === h.id}
                    data-testid={`hot-lead-contacted-${h.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-orange-300 text-orange-700 bg-white hover:bg-orange-50 disabled:opacity-50">
                    <CheckCircle size={13} /> Mark contacted
                  </button>
                  <button onClick={() => dismiss(h.id)} disabled={busy === h.id}
                    title="Just curious, not a real lead — remove from this list"
                    data-testid={`hot-lead-dismiss-${h.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md border border-zinc-200 text-zinc-500 bg-white hover:bg-zinc-50 hover:text-zinc-700 disabled:opacity-50">
                    <X size={13} /> Discard
                  </button>
                </div>
              ) : (
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
                  <CheckCircle size={12} weight="fill" /> Contacted
                </span>
              )}
              </div>
              {expanded === h.id && (
                <div className="mt-2 ml-9 rounded-md border border-orange-100 bg-white/70 px-3 py-2" data-testid={`hot-lead-timeline-${h.id}`}>
                  {!timelines[h.contact_id] ? (
                    <div className="text-[11px] text-zinc-400 py-1">Loading interactions…</div>
                  ) : timelines[h.contact_id].length === 0 ? (
                    <div className="text-[11px] text-zinc-400 py-1">No recorded interactions.</div>
                  ) : (
                    <>
                      <ul className="divide-y divide-zinc-100">
                        {timelines[h.contact_id].slice(0, 15).map((ev, i) => <TimelineRow key={ev.id || i} ev={ev} />)}
                      </ul>
                      {timelines[h.contact_id].length > 15 && (
                        <Link to={`/contacts/${h.contact.id}`} className="block text-[10px] text-zinc-400 hover:text-zinc-600 hover:underline pt-1">
                          Showing latest 15 of {timelines[h.contact_id].length} · open full profile for everything
                        </Link>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {followUp && <FollowUpDialog lead={followUp} onClose={() => setFollowUp(null)} onSent={() => { setFollowUp(null); load(tab); }} />}
    </div>
  );
};
